import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { apiService } from '../services/api.service';
import { useDeviceDispatch } from './DeviceContext';
import { useProfileState } from './ProfileContext';
import { buildSignalRHub, deviceHub } from '../services/signalr.service';

let lastUpdated = '';

export const DeviceData = () => {
	const { loading } = useProfileState();
	const dispatch = useDeviceDispatch();
	const { enqueueSnackbar } = useSnackbar();

	const getData = () => {
		console.log('Starting device context fill...');
		dispatch({ type: 'LOADING' });
		getDevices()
			.then((result) => {
				lastUpdated = result.lastUpdated;
				dispatch({
					type: 'INIT_LOAD',
					payload: {
						peblGateways: result.pebl.gateway,
						vyneGateways: result.vyne.gateway,
						vyneNodes: result.vyne.node,
						count: result.count,
						claimPendingCount: result.claimPendingCount,
						ownedDeviceCount: result.ownedDeviceCount,
						onlineGateways: result.onlineGateways,
					},
				});
				startSignalR();
			})
			.catch((error) => {
				console.error(error);
				enqueueSnackbar('Failed to load devices', { variant: 'error' });
			});
	};

	const updateDevices = () => {
		getDevices(lastUpdated)
			.then((result) => {
				lastUpdated = result.lastUpdated;
				dispatch({
					type: 'UPDATE_DEVICES',
					payload: {
						peblGateways: result.pebl.gateway,
						vyneGateways: result.vyne.gateway,
						vyneNodes: result.vyne.node,
						count: result.count,
						claimPendingCount: result.claimPendingCount,
						ownedDeviceCount: result.ownedDeviceCount,
						onlineGateways: result.onlineGateways,
					},
				});
			})
			.catch((error) => {
				enqueueSnackbar('Failed to update devices', { variant: 'error' });
			});
	};

	const getDevices = (lastUpdated) => {
		let path = '/device';

		if (lastUpdated) {
			path = `/device?lastUpdated=${lastUpdated}`;
		}

		return new Promise((resolve, reject) => {
			apiService
				.getData(path)
				.then((result) => {
					resolve(result);
				})
				.catch((error) => {
					dispatch({ type: 'LOADED' });
					enqueueSnackbar(error, { variant: 'error' });
					reject(false);
				});
		});
	};

	const startSignalR = () => {
		buildSignalRHub();
		// Capture this specific connection. buildSignalRHub() reassigns the shared
		// `deviceHub` on every (re)start (e.g. an org switch), so the start
		// callback below must act on the instance it actually started — not
		// whatever `deviceHub` points to by the time the promise resolves — or we
		// can invoke on a superseded, not-yet-connected hub.
		const hub = deviceHub;
		if (hub.state === 'Disconnected') {
			hub
				.start()
				.then(() => {
					// Bail if this hub was superseded (another start ran) or was
					// stopped during startup — invoking now would throw "not Connected".
					if (hub !== deviceHub || hub.state !== 'Connected') {
						return;
					}
					console.log('SignalR started');
					hub.invoke('AddToOrganisation')
						.then((res) => {
							console.log('SignalR added to org', res);
						})
						.catch((error) => {
							console.warn('SignalR AddToOrganisation failed', error);
						});
					hub.on('DeviceUpdate', () => {
						updateDevices();
					});
					hub.on('DeviceRemoved', (msg) => {
						dispatch({ type: 'REMOVE_DEVICES', payload: msg.devices });
					});
				})
				.catch((error) => {
					// A start aborted by an org switch (we stopped it) or superseded by
					// a newer connection isn't a real failure — don't alarm the user.
					if (hub === deviceHub && !localStorage.getItem('switchingOrganisation')) {
						console.error('SignalR failed', error);
						enqueueSnackbar(
							'Live updates disconnected, please refresh the page',
							{ variant: 'error', persist: true }
						);
					}
				});
			hub.onclose(() => {
				var switching = localStorage.getItem('switchingOrganisation');
				if (!switching) {
					console.log('SignalR disconnected');
					enqueueSnackbar(
						'Live updates disconnected, please refresh the page',
						{ variant: 'error', persist: true }
					);
				} else {
					console.log('SignalR closed but swtiching');
					localStorage.removeItem('switchingOrganisation');
				}
			});
		}
	};

	return {
		getData: getData,
	};
};
